<template>
  <div>
    <mds-fieldset
      legend="Repeat"
      class="frequency-modal__monthly-fieldset"
      variation="radio-group"
    >
      <mds-radio-button
        v-model="selectedMonthlyOpt"
        class="frequency-modal__radiobuttons"
        value="0"
        :checked="checkedOption('0')"
      >
        <mds-combo-box
          v-model="selectedFixedDay"
          class="frequency-modal__combobox"
          label=""
          :disabled="disableSelection('0')"
          :data-set="fixedDays"
          @input="$emit('input', { fixedDay: $event[0] })"
        />
        <mds-combo-box
          class="frequency-modal__combobox"
          v-model="selectedDateType"
          label=""
          :disabled="disableSelection('0')"
          :data-set="dateTypes"
          :selected-items="selectedDateType"
          @input="$emit('input', { dateType: $event[0] })"
        />
        <div class="frequency-modal__monthly-label">
          {{ translate('frequency.modal.monthly.label') }}
        </div>
      </mds-radio-button>
      <mds-radio-button
        v-model="selectedMonthlyOpt"
        class="frequency-modal__radiobuttons"
        value="1"
        :checked="checkedOption('1')"
      >
        <mds-combo-box
          v-model="selectedOrdinal"
          class="frequency-modal__combobox"
          label=""
          :disabled="disableSelection('1')"
          :data-set="ordinalNumbers"
          @input="$emit('input', { ordinal: $event[0] })"
        />
        <mds-combo-box
          v-model="selectedWeekday"
          class="frequency-modal__combobox"
          label=""
          :disabled="disableSelection('1')"
          :data-set="dayList"
          @input="$emit('input', { weekday: $event[0] })"
        />
        <div class="frequency-modal__monthly-label">
          {{ translate('frequency.modal.monthly.label') }}
        </div>
      </mds-radio-button>
    </mds-fieldset>
    <holidays-field
      class="frequency-modal__holidays"
      v-if="selectedDateType[0] === 'Holiday'"
      :selected-data="selectedData"
      @selectedCountry="selectedCountry = $event"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import toArray from 'lodash/toArray'
import MdsComboBox from '@mds/combo-box'
import MdsFieldset from '@mds/fieldset'
import MdsRadioButton from '@mds/radio-button'
import HolidaysField from '@/components/frequencyModal/frequencies/helpers/HolidaysField.vue'
import { dayList } from '@/utils/getter'
import { toOrdinal } from 'ordinal-js'
import MwcBaseHelpersMixin from '@morningstar/mcl-utilities/src/vue/mixins/mwc-base-mixin'
import defaultConfig from '@/components/frequencyModal/config/i18n'
import {
  DAY_TYPES,
  MONTHLY_OPTIONS,
  DAY_OPTIONS,
  FREQUENCIES,
  WEEK_DAYS,
} from '@/components/frequencyModal/config/constants'

export default {
  name: 'MonthlyFrequency',
  components: {
    MdsComboBox,
    MdsFieldset,
    MdsRadioButton,
    HolidaysField,
  },
  props: {
    dateType: {
      type: String,
      default: '',
    },
    fixedDay: {
      type: String,
      default: '',
    },
    ordinal: {
      type: String,
      default: '',
    },
    weekday: {
      type: String,
      default: '',
    },
  },
  mixins: [MwcBaseHelpersMixin],
  data() {
    return {
      defaultConfig,
      selectedMonthlyOpt: '',
      selectedDateType: [],
      selectedFixedDay: [],
      selectedOrdinal: [],
      selectedWeekday: [],
      dayList: dayList(),
      selectedCountry: '',
    }
  },
  created() {
    const isMonthly =
      this.editedSchedule?.frequency?.repeatType === FREQUENCIES.Monthly
    const isFirstOption = this.dateType && this.fixedDay

    this.selectedDateType = [
      !isMonthly || !isFirstOption
        ? Object.keys(DAY_TYPES)[0]
        : this.changeDateType,
    ]
    this.selectedFixedDay = [
      this.fixedDay &&
      this.fixedDays.find((fixedDay) => fixedDay.value === this.fixedDay)
        ? this.fixedDay
        : this.fixedDays[0].value,
    ]
    this.selectedOrdinal = [this.ordinal || Object.values(DAY_OPTIONS)[0]]
    this.selectedWeekday = [this.weekday || Object.keys(WEEK_DAYS)[1]]

    this.selectedMonthlyOpt =
      this.dateType ||
      this.fixedDay ||
      !this.editedSchedule ||
      this.editedSchedule?.frequency?.repeatType !== FREQUENCIES.Monthly
        ? MONTHLY_OPTIONS[0]
        : MONTHLY_OPTIONS[1]
    this.emitMonthlySelection()
  },
  watch: {
    selectedMonthlyOpt: {
      handler: 'emitMonthlySelection',
    },
    selectedCountry: {
      handler: 'emitMonthlySelection',
    },
  },
  computed: {
    ...mapState({
      editedSchedule: (state) => state.cache.cache.editedSchedule,
      holidays: (state) => state.cache.cache.holidays,
    }),
    dateTypes() {
      const textMapping = {
        Calendar: this.translate('frequency.modal.everyday.text'),
        Business: this.translate('frequency.modal.weekday.text'),
        Holiday: `${this.translate(
          'frequency.modal.holidays.text'
        )} (excl. holidays)`,
      }
      return Object.keys(DAY_TYPES).map((key) => ({
        text: textMapping[key],
        value: key,
      }))
    },
    fixedDays() {
      if (this.selectedDateType[0]) {
        const time =
          DAY_TYPES[
            this.selectedDateType[0].includes('-Business')
              ? 'Holiday'
              : this.selectedDateType[0]
          ]
        const fnText = toOrdinal
        const fnValue = (val) => `${val}`
        return toArray(this.getArraySize(time)).map((_, value) => ({
          text: fnText(value + 1),
          value: fnValue(value + 1),
        }))
      }
      return []
    },
    ordinalNumbers() {
      const fnText = (value) =>
        this.translate(`frequency.modal.options.number.text.${value}`)
      const fnValue = (value) =>
        this.translate(`frequency.modal.options.number.value.${value}`)
      return toArray(this.getArraySize(5)).map((_, value) => ({
        text: fnText(value + 1),
        value: fnValue(value + 1),
      }))
    },
    compositeDateType() {
      return this.selectedDateType[0] === 'Holiday'
        ? `${this.selectedCountry}-Business`
        : this.selectedDateType[0]
    },
    changeDateType() {
      return this.dateType.includes('-Business') ? 'Holiday' : this.dateType
    },
    selectedData() {
      if (this.editedSchedule?.frequency?.repeatType === 'Monthly') {
        return this.dateType
      }
      return ''
    },
  },
  methods: {
    checkedOption(type) {
      return type === MONTHLY_OPTIONS[0]
    },
    disableSelection(type) {
      return type !== this.selectedMonthlyOpt
    },
    emitMonthlySelection() {
      if (this.selectedMonthlyOpt === MONTHLY_OPTIONS[0]) {
        this.$emit('input', { fixedDay: this.selectedFixedDay[0] })
        this.$emit('input', { dateType: this.compositeDateType })
      } else {
        this.$emit('input', { ordinal: this.selectedOrdinal[0] })
        this.$emit('input', { weekday: this.selectedWeekday[0] })
      }
      this.$emit('input', { monthlyOpt: this.selectedMonthlyOpt })
    },
    getArraySize(size) {
      return toArray({ length: size }, (_, index) => index + 1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@mds/constants';
@import '@mds/typography';

.frequency-modal {
  &__monthly-fieldset {
    &::v-deep [class^='mds-fieldset__legend'] {
      margin-bottom: $mds-space-1-x;
    }
    display: flex;
    flex-direction: column;
  }
  &__radiobuttons {
    &::v-deep [class^='mds-radio-button__visible'] {
      align-items: baseline;
      gap: $mds-space-1-and-a-half-x;
      width: 100%;
    }
    &::v-deep [class^='mds-radio-button__text'] {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: $mds-space-1-and-a-half-x;
      width: 70%;
    }
  }
  &__combobox {
    flex: 1;
  }
  &__monthly-label {
    padding-top: $mds-space-1-and-a-half-x;
  }
  &__holidays {
    background-color: $mds-feedback-color-info;
    margin-top: $mds-space-2-x;
    padding: $mds-space-1-and-a-half-x;
  }
}
</style>
